import { TransformType } from '@mr-yum/cdn-image'
import { navigate } from '@mr-yum/frontend-core/dist/services/routes'
import { ChevronDownIcon } from '@mr-yum/frontend-ui'
import cn from 'classnames'
import { Image } from 'components/Shared/Image'
import { CategoryMenuBarContext } from 'contexts/CategoryMenuBarContext'
import { DeviceContext } from 'contexts/DeviceContext'
import { MenuQuery } from 'lib/gql'
import { OrderingTypeSlug, routes } from 'lib/routes'
import { observer } from 'mobx-react-lite'
import React, {
  createRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-scroll'
import { MenuStoreContext } from 'stores/MenuStore'

import { CategorySheet } from './CategorySheet'
import { useFilteredMenuSections } from './hooks/useFilteredMenuSections'

interface Props {
  venueSlug: string
  orderingTypeSlug: OrderingTypeSlug
  menuCategory: MenuQuery['guestMenuCategory']
  menuCategories: MenuQuery['guestMenuCategories']
  children?: ReactNode
}

const OFFSET_FOR_NAV_HEIGHT = -132
const FLEXIBLE_WAIT_TIMES_OFFSET = 40

export const CategoryMenuBar = observer(
  ({
    menuCategory,
    venueSlug,
    orderingTypeSlug,
    menuCategories,
    children,
  }: Props) => {
    const [categorySheetOpen, setCategorySheetOpen] = useState(false)
    const { setSearchQuery } = useContext(MenuStoreContext)
    const [categoryImageLoaded, setCategoryImageLoaded] = useState(false)
    const {
      currentSectionIndex,
      setSectionButtonClicked,
      setCurrentSectionIndexes,
    } = useContext(CategoryMenuBarContext)
    const { iOS } = useContext(DeviceContext)
    const intl = useIntl()

    const menuSections = useFilteredMenuSections(menuCategory)

    const menuCategoryImage = menuCategory.image

    const menuSectionsWithRef = menuSections.map(() =>
      createRef<HTMLDivElement>(),
    )

    useEffect(() => {
      const menuSectionRef = menuSectionsWithRef[currentSectionIndex]

      if (!menuSectionRef) return

      const currentMenuSectionRef = menuSectionRef.current

      if (!currentMenuSectionRef) return

      currentMenuSectionRef.scrollIntoView({
        inline: currentSectionIndex === 0 ? 'end' : 'start',
        behavior: iOS ? 'smooth' : 'auto',
      })
    }, [currentSectionIndex, iOS, menuSectionsWithRef])

    const handleCategoryChange = useCallback(
      (categorySlug: string) => {
        /**
         * We need to reset searchQuery when changing category
         */
        setSearchQuery('')
        void navigate(routes.menuCategory, {
          venueSlug,
          orderingTypeSlug,
          category: categorySlug,
        }).then(() => {
          window.scrollTo(0, 0)
        })
      },
      [orderingTypeSlug, setSearchQuery, venueSlug],
    )

    const handleCategorySelect = useCallback(
      (categorySlug: string) => () => {
        setCategorySheetOpen(false)
        setCategoryImageLoaded(false)
        handleCategoryChange(categorySlug)
      },
      [handleCategoryChange],
    )

    return (
      <div className="js-dialog-offset border-b text-foreground">
        <div className="ml-4 flex h-14 items-center overflow-x-auto whitespace-nowrap pr-4 scrollbar-hide">
          <button
            type="button"
            className={cn(
              'h-9 flex-shrink-0 rounded border bg-surface-subtle pr-2 text-foreground transition-colors my-label-sm hover:bg-surface',
              {
                'pl-1': menuCategoryImage,
                'pl-3': !menuCategoryImage,
              },
            )}
            onClick={() => setCategorySheetOpen(true)}
            aria-label={intl.formatMessage({
              defaultMessage: 'Open category modal',
              id: '9M4OON',
            })}
          >
            <span className="flex items-center space-x-2">
              {menuCategoryImage && (
                <span className="relative h-7 w-7">
                  <Image
                    image={menuCategoryImage}
                    alt={menuCategory.name}
                    transform={TransformType.SQUARE}
                    priority
                    className={cn('rounded-sm', {
                      'opacity-1': categoryImageLoaded,
                      'opacity-0': !categoryImageLoaded,
                    })}
                    layout="fixed"
                    width={28}
                    height={28}
                    onLoad={() => setCategoryImageLoaded(true)}
                  />
                  {!categoryImageLoaded && (
                    <span className="bg-suface-subtle absolute left-0 top-0 h-full w-full animate-pulse object-cover"></span>
                  )}
                </span>
              )}
              <span>{menuCategory.name}</span>
              <ChevronDownIcon />
            </span>
          </button>

          {menuSections && (
            <div className="flex w-full space-x-2 px-2">
              {menuSections.map((menuSection, index) => {
                return (
                  <div
                    key={menuSection.id}
                    ref={menuSectionsWithRef[index]}
                    className="last:pr-4 hover:cursor-pointer"
                  >
                    <Link
                      to={menuSection.slug}
                      id={menuSection.id}
                      duration={100}
                      smooth={true}
                      offset={
                        children
                          ? OFFSET_FOR_NAV_HEIGHT - FLEXIBLE_WAIT_TIMES_OFFSET
                          : OFFSET_FOR_NAV_HEIGHT
                      }
                      onClick={() => {
                        setSectionButtonClicked(true)
                        setCurrentSectionIndexes([index])
                        setTimeout(() => {
                          setSectionButtonClicked(false)
                        }, 150)
                      }}
                    >
                      <span
                        className={cn(
                          'flex h-9 items-center justify-center rounded border px-4 py-2 transition-colors',
                          {
                            'bg-surface-subtle text-foreground my-body-sm hover:bg-surface':
                              currentSectionIndex !== index,
                          },
                          {
                            'border-[theme(backgroundColor.surface-inverted) bg-surface-inverted text-foreground-inverted my-label-sm':
                              currentSectionIndex === index,
                          },
                        )}
                      >
                        {menuSection.name}
                      </span>
                    </Link>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        {children && <div className="px-4 pb-3">{children}</div>}
        <CategorySheet
          categorySheetOpen={categorySheetOpen}
          setCategorySheetOpen={setCategorySheetOpen}
          menuCategories={menuCategories}
          activeCategory={menuCategory}
          handleCategorySelect={handleCategorySelect}
        />
      </div>
    )
  },
)

CategoryMenuBar.displayName = 'CategoryMenuBar'
